import axios from "../setupAxios";

// Mock API functions

// Simple browser console logging — gives visibility into every API call's
// success/failure, since there is no client-side error telemetry today.
function logApiSuccess(label, extra) {
  console.log(`[order-api] ${label} OK`, extra);
}

function logApiError(label, error) {
  console.error(`[order-api] ${label} FAILED`, {
    status: error.response?.status,
    data: error.response?.data,
    message: error.message,
  });
}

export const calculateTotalAmount = async (formData) => {
    const shareholdersCount = formData?.orderLines?.map(c => {
        return `shareholderCount=${c.shareholderCount}`
    });
    try {
        let frequency = "monthly"
        if (formData?.billingFrequency === "Anual") {
            frequency = "annually"
        }

        const { data } = await axios.get(`/api/v1/order/OrderResume?${shareholdersCount.join("&")}&billingFrequency=${frequency}&couponCode=${formData?.couponCode}`,
            {
                withCredentials: false,
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            }
        );
        logApiSuccess('calculateTotalAmount', data);
        return data;
    } catch (error) {
        logApiError('calculateTotalAmount', error);
        console.warn('[order-api] OrderResume unavailable, using local fallback calculation.');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Example calculation
    const baseAmount = formData.numberOforderLines * 100; // R$100 per company
    const monthly = baseAmount;
    const annually = baseAmount * 10; // 2 months free

    const fallback = { monthly, annually };
    console.warn('[order-api] calculateTotalAmount USING LOCAL FALLBACK', fallback);
    return fallback;
};


export const validateTaxId = async (taxId) => {
    if (!taxId) {
      throw new Error('CNPJ é obrigatório');
    }
    const encodedTaxId = encodeURIComponent(taxId);
    try {
      const { data } = await axios.get(`/api/v1/order/ValidateTaxId/${encodedTaxId}`);
      logApiSuccess('validateTaxId', data);
      return data; // Supondo que o endpoint retorne o Tax ID formatado
    } catch (error) {
      logApiError('validateTaxId', error);

      // Check the error's HTTP status code
      const status = error.response?.status;

      if (status === 400) {
        throw new Error('CNPJ não encontrado ou inválido');
      } else if (status === 409) {
        throw new Error('Esta companhia já existe no nosso sistema. Contate a equipe comercial');
      }

      // Generic fallback for any other error
      throw new Error('Falha ao validar o CNPJ');
    }
  };
  
export const validateCoupon = async (code) => {

    try {
        const { data } = await axios.get(`/api/v1/order/ValidateCoupon/${code}`);
        logApiSuccess('validateCoupon', data);
        return data;
    } catch (error) {
        logApiError('validateCoupon', error);
        // Return the backend's message (string), never the Error object — the caller
        // distinguishes success from failure by checking whether the return value is
        // an object (coupon data) or a string (failure reason).
        return error.response?.data || error.message || 'Cupom inválido';
    }
};
  
export const fetchBillingDetails = async (taxId) => {
  const sanitizedTaxId = taxId.replace(/\D/g, '').slice(0, 14);

  try {
    // axios automatically rejects for any non-2xx status, so a successful
    // return here already guarantees response.status === 200.
    const response = await axios.get(`https://open.cnpja.com/office/${sanitizedTaxId}`);
    logApiSuccess('fetchBillingDetails', response.data);
    return response.data;
  } catch (error) {
    logApiError('fetchBillingDetails', error);

    // Check whether this is an HTTP error
    const status = error.response?.status;

    if (status === 400 || status === 404) {
      throw new Error('CNPJ não encontrado no banco de dados');
    } else if (status === 429) {
      throw new Error('Muitas requisições. Tente novamente em alguns instantes');
    }

    // Generic fallback (network error, timeout, 5xx, etc.)
    throw new Error('Erro ao obter detalhes do CNPJ na Receita Federal');
  }
};

export const submitOrder = async (formData) => {
    try {
        const body = transformFormData(formData);
        const { data } = await axios.post(`/api/v1/order/`, body);

        logApiSuccess('submitOrder', data);
        return data;
    } catch (error) {
        logApiError('submitOrder', error);
        throw new Error(error.response?.data || error.message || 'Falha ao enviar pedido.');
    }
};

function transformFormData(formData) {
  const cleanPaymentMethod = formData.paymentMethod.replace(/\s+/g, "");
  const payload = {
    billingFrequency: formData.billingFrequency === "Mensal" ? "Monthly" : "Annually",
    trialPeriod: formData.trialPeriod,
    paymentMethod: cleanPaymentMethod,
    couponCode: formData.couponCode,
    billingTaxId: formData.billingTaxId,
    BillingName: formData.BillingName,
    BillingLegalName: formData.BillingLegalName,
    billingAddress: {
      addressLine1: formData.billingAddress.addressLine1,
      addressLine2: formData.billingAddress.addressLine2,
      city: formData.billingAddress.city,
      state: formData.billingAddress.state,
      postalCode: formatPostalCode(formData.billingAddress.postalCode),
      country: formData.billingAddress.country,
    },
    administrationContact: {
      firstName: formData.administrationContact.firstName,
      lastName: formData.administrationContact.lastName,
      email: formData.administrationContact.email,
      mobilePhone: formData.administrationContact.mobilePhone,
    },
    billingContact: {
      firstName: formData.billingContact.firstName,
      lastName: formData.billingContact.lastName,
      email: formData.billingContact.email,
      businessPhone: formData.billingContact.businessPhone,
    },
    saleSource: getSaleSourceValue(formData.saleSource),
    saleSourceCampaign: formData.saleSourceCampaign,
    notes: formData.notes,
    orderLines: formData.orderLines.map(line => ({
      taxId: line.taxId,
      name: line.name,
      shareholderCount: Number(line.shareholderCount),
    }))
  };
  if (formData.couponCodeAdditionalInfo) {
    payload.couponCodeAdditionalInfo = formData.couponCodeAdditionalInfo;
  }

  return payload;
}

const getSaleSourceValue = (key) => {
  const saleSourceMap = {
    "Pesquisa orgânica": "OrganicSearch",
    "Pesquisa paga": "PaidSearch",
    "Protocolo de Parceiro": "PartnerProtocol",
    "Conteúdo de Parceiros": "PartnerContent",
    "Email Marketing": "EmailMarketing",
    "Social Orgânico": "OrganicSocial",
    "Social pago": "PaidSocial",
    "Indicação de clientes": "CustomerReferral",
    "Fontes off-line": "OfflineSources",
  };

  return saleSourceMap[key] || "OrganicSearch";
};

function formatPostalCode(postalCode) {
  if (postalCode.length === 8) {
    return postalCode.slice(0, 5) + '-' + postalCode.slice(5);
  }
  return postalCode;
}
